// Route: /notes — список конспектов («Картотека»).
// URL state: ?q, ?material, ?level, ?date, ?demo. Вид (карточки/список) — в
// localStorage (useViewMode), не в URL: запоминается между визитами.
//
// См. docs/ui-spec/notes.list.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, EmptyState, ErrorState, SectionHeader, Skeleton } from "@studyhub/ui";
import { GenerationProgressRing } from "@studyhub/ui/components/studyhub/generation-progress-ring";
import { toast } from "@studyhub/ui/toast";
import { IconLoader2, IconNotebook, IconPlus, IconSparkles } from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useMaterialsList } from "@/components/materials/_api";
import {
	type NoteDetailLevel,
	type NoteDto,
	useDeleteNote,
	useNotesList,
} from "@/components/notes/_api";
import { GenerateNoteDialog } from "@/components/notes/editor/generate-dialog";
import { DetailLevelBadge } from "@/components/notes/notes-list/detail-level-badge";
import {
	NotesFilterPills,
	NotesFilterSheetContent,
	type NotesFilters,
	notesFiltersActive,
} from "@/components/notes/notes-list/filters-bar";
import { NoteCard } from "@/components/notes/notes-list/note-card";
import {
	CreateEmptyTile,
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useGenerationProgress } from "@/hooks/use-generation-progress";
import { useViewMode } from "@/hooks/use-view-mode";

// Лёгкий веер наклонов плиток grid — «живая» картотека.
const TILTS = [-1.2, 0.8, -0.6, 1, -0.9, 0.6] as const;

const searchSchema = z.object({
	q: z.string().optional(),
	material: z.string().optional(),
	level: z.enum(["summarized", "in-depth", "comprehensive", "manual"]).optional(),
	date: z.enum(["today", "week", "month", "all"]).optional(),
	demo: z
		.enum(["default", "loading", "empty", "error", "streaming", "quota-exceeded", "paywall"])
		.optional(),
});

export const Route = createFileRoute("/notes/")({
	validateSearch: tolerantSearch(searchSchema),
	component: NotesListPage,
	pendingComponent: PagePendingFallback,
});

function NotesListPage(): React.ReactElement {
	const { t } = useTranslation("notes");
	const { t: tc } = useTranslation("common");
	const search = useSearch({ from: "/notes/" });
	const navigate = useNavigate({ from: "/notes/" });
	const intro = useSectionIntro("notes");
	const [generateOpen, setGenerateOpen] = useState(false);
	const [view, setView] = useViewMode("notes");

	const notesQuery = useNotesList();
	const materialsQuery = useMaterialsList();
	const deleteNote = useDeleteNote();
	const materials = useMemo(() => materialsQuery.data?.items ?? [], [materialsQuery.data]);
	const materialMap = useMemo(() => new Map(materials.map((m) => [m.id, m.title])), [materials]);

	const filters: NotesFilters = useMemo(
		() => ({
			q: search.q,
			materialId: search.material,
			detailLevel: search.level ?? "all",
			dateRange: search.date ?? "all",
		}),
		[search],
	);

	function updateFilters(next: NotesFilters): void {
		void navigate({
			search: (prev) => ({
				...prev,
				q: next.q || undefined,
				material: next.materialId,
				level: next.detailLevel === "all" ? undefined : (next.detailLevel as NoteDetailLevel),
				date: next.dateRange === "all" ? undefined : next.dateRange,
			}),
			replace: true,
		});
	}

	const isLoading = search.demo === "loading" || notesQuery.isLoading;
	const isError = search.demo === "error" || notesQuery.isError;

	const allNotes = notesQuery.data?.items ?? [];
	const notes = useMemo(() => {
		if (search.demo === "empty") return [];
		let items: NoteDto[] = allNotes;
		if (filters.q) {
			const q = filters.q.toLowerCase();
			items = items.filter((n) => n.title.toLowerCase().includes(q));
		}
		if (filters.materialId) items = items.filter((n) => n.materialId === filters.materialId);
		if (filters.detailLevel && filters.detailLevel !== "all")
			items = items.filter((n) => n.detailLevel === filters.detailLevel);
		if (filters.dateRange && filters.dateRange !== "all") {
			const cutoff =
				Date.now() -
				(filters.dateRange === "today"
					? 86_400_000
					: filters.dateRange === "week"
						? 7 * 86_400_000
						: 30 * 86_400_000);
			items = items.filter((n) => new Date(n.updatedAt).getTime() >= cutoff);
		}
		return items;
	}, [allNotes, filters, search.demo]);

	const showGeneratingPlaceholder = search.demo === "streaming";
	const hasActiveFilters = !!(
		filters.q ||
		filters.materialId ||
		filters.detailLevel !== "all" ||
		filters.dateRange !== "all"
	);

	const containerClass = view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				category="ai"
				icon={<IconNotebook />}
				title={t("list.title")}
				description={t("list.subtitle", {
					notes: notes.length,
					materials: materials.length,
				})}
				actions={
					<div className="flex items-center gap-2">
						<SectionIntroReopen intro={intro} />
						<Button variant="outline" size="default" asChild className="cursor-pointer">
							<Link to="/notes/$noteId" params={{ noteId: "new" }} search={{ edit: 1 }}>
								<IconPlus size={16} />
								<span className="ml-1.5 hidden sm:inline">{t("list.actions.createBlank")}</span>
							</Link>
						</Button>
						<Button size="default" onClick={() => setGenerateOpen(true)} className="cursor-pointer">
							<IconSparkles size={16} />
							<span className="ml-1.5 hidden sm:inline">{t("list.actions.generate")}</span>
						</Button>
					</div>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			<ListToolbar
				search={{
					value: filters.q ?? "",
					onChange: (q) => updateFilters({ ...filters, q }),
					placeholder: t("list.filters.searchPlaceholder"),
				}}
				filters={
					<NotesFilterPills
						filters={filters}
						onChange={updateFilters}
						materials={materials.map((m) => ({ id: m.id, title: m.title }))}
					/>
				}
				filtersSheet={
					<NotesFilterSheetContent
						filters={filters}
						onChange={updateFilters}
						materials={materials.map((m) => ({ id: m.id, title: m.title }))}
					/>
				}
				filtersActive={notesFiltersActive(filters)}
				view={view}
				onViewChange={setView}
			/>

			{isLoading ? (
				<ListSkeleton view={view} />
			) : isError ? (
				<ErrorState
					title={t("list.errorTitle")}
					description={t("list.errorHint")}
					action={
						<Button
							variant="outline"
							onClick={() => notesQuery.refetch()}
							className="cursor-pointer"
						>
							{tc("actions.retry")}
						</Button>
					}
				/>
			) : notes.length === 0 ? (
				hasActiveFilters ? (
					<EmptyState
						title={t("list.emptyFiltered")}
						action={
							<Button
								variant="outline"
								onClick={() => updateFilters({ detailLevel: "all", dateRange: "all" })}
								className="cursor-pointer"
							>
								{t("list.resetFilters")}
							</Button>
						}
					/>
				) : (
					<EmptyState
						icon={<IconNotebook />}
						title={t("list.empty")}
						description={t("list.emptyDescription")}
						action={
							<Button onClick={() => setGenerateOpen(true)} className="cursor-pointer">
								<IconSparkles size={16} />
								{t("list.actions.generate")}
							</Button>
						}
					/>
				)
			) : (
				<div className={containerClass}>
					{showGeneratingPlaceholder && <GeneratingPlaceholder />}
					{notes.map((note, i) => (
						<NoteCard
							key={note.id}
							note={note}
							view={view}
							tilt={view === "grid" ? TILTS[i % TILTS.length] : undefined}
							materialTitle={
								note.materialName ??
								(note.materialId ? materialMap.get(note.materialId) : undefined)
							}
							onDelete={(id) =>
								deleteNote.mutate(id, {
									onError: () => toast.error(t("list.errorTitle")),
								})
							}
						/>
					))}
					{/* Тактильная плитка «Создать пустой» — только в grid (как в /flashcards). */}
					{view === "grid" && (
						<CreateEmptyTile
							category="ai"
							icon={<IconNotebook />}
							label={t("list.actions.createBlank")}
							onClick={() =>
								void navigate({
									to: "/notes/$noteId",
									params: { noteId: "new" },
									search: { edit: 1 },
								})
							}
						/>
					)}
				</div>
			)}

			{search.demo === "paywall" && (
				<div className="mt-4 rounded-[var(--radius-card)] border border-warning/30 bg-warning/5 p-3 text-sm">
					Paywall demo — открой UpgradeModal через Settings → Billing.
				</div>
			)}

			<GenerateNoteDialog
				open={generateOpen}
				onOpenChange={setGenerateOpen}
				demoQuotaUsed={search.demo === "quota-exceeded" ? 5 : 2}
				demoQuotaLimit={5}
			/>
		</div>
	);
}

function ListSkeleton({ view }: { view: "grid" | "list" }) {
	if (view === "grid") {
		return (
			<div className={LIST_PAGE_GRID} aria-busy="true">
				{Array.from({ length: 6 }).map((_, i) => (
					<Skeleton key={i} className="h-44 rounded-[var(--radius-card)]" />
				))}
			</div>
		);
	}
	return (
		<div className={LIST_PAGE_STACK} aria-busy="true">
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={i}
					className="flex items-center gap-3 rounded-[var(--radius-card)] border border-border bg-card p-3.5 shadow-e1"
				>
					<Skeleton className="size-10 shrink-0 rounded-[var(--radius-tile)]" />
					<div className="flex-1 space-y-2">
						<div className="flex items-center justify-between gap-3">
							<Skeleton className="h-4 w-2/5" />
							<Skeleton className="h-4 w-16" />
						</div>
						<Skeleton className="h-3.5 w-4/5" />
						<Skeleton className="h-3 w-1/3" />
					</div>
				</div>
			))}
		</div>
	);
}

function GeneratingPlaceholder() {
	const { t } = useTranslation("notes");
	const { t: tc } = useTranslation("common");
	const { progress, percent, applyTick } = useGenerationProgress({
		defaultCrawlMs: 40_000,
		crawlCap: 0.9,
	});
	useEffect(() => {
		applyTick({ progress: 0.1, crawlMs: 40_000 });
	}, [applyTick]);
	return (
		<div className="flex items-center gap-3 rounded-[var(--radius-card)] border border-primary/35 bg-primary/[0.04] p-3.5">
			<GenerationProgressRing
				progress={progress}
				size={40}
				label={tc("generation.progress", { percent, defaultValue: `${percent}%` })}
			/>
			<div className="min-w-0 flex-1 space-y-1.5">
				<div className="flex items-center justify-between gap-2">
					<span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
						<IconLoader2 size={14} className="animate-spin text-primary" />
						{t("generation.inProgress")}
					</span>
					<DetailLevelBadge level="comprehensive" />
				</div>
				<p className="text-xs text-muted-foreground">
					{t("generation.stage", { stage: "rerank" })}
				</p>
			</div>
		</div>
	);
}
